import * as _ from 'lodash-es'
import { defineStore } from 'pinia'
import { Ref, computed, ref, toRefs, watch } from 'vue'

import api from '@/api/api'
import {
  TABLE_UPDATE_STATUS,
  TableStatus,
} from '@/modules/performance/interfaces/PerformanceTableInterfaces'
import {
  PerformanceTableWithReservations,
  usePerformanceReservationStore,
} from '@/modules/performance/stores/PerformanceReservationStore'
import { useSettingStore } from '@/stores/setting'
import { deleteById, nextName, upsertById } from '@/utils/utils'

import {
  AddOnRead,
  PerformanceAddOnId,
  PerformanceAddOnRead,
  PerformanceReservationsRead,
  PerformanceSeatingTimeId,
  PerformanceSeatingTimeRead,
  PerformanceTableId,
  PerformanceTableRead,
  TableShape,
} from '@generated/types'

export const usePerformanceUpdateStore = defineStore('performanceUpdate', () => {
  const updatedPerformance: Ref<PerformanceReservationsRead | null> = ref(null)
  const performanceReservationStore = usePerformanceReservationStore()
  const { selectedPerformance } = toRefs(performanceReservationStore)
  watch(
    selectedPerformance,
    () => {
      updatedPerformance.value = _.cloneDeep(selectedPerformance.value)
    },
    { immediate: true, deep: true },
  )

  // S C H E D U L E
  const isDatetimeUpdated = computed(() => {
    if (!updatedPerformance.value || !selectedPerformance.value) return false
    return selectedPerformance.value.datetime !== updatedPerformance.value.datetime
  })
  const isPublishDatetimeUpdated = computed(() => {
    if (!updatedPerformance.value || !selectedPerformance.value) return false
    return selectedPerformance.value.publish_datetime !== updatedPerformance.value.publish_datetime
  })
  const isSchedulePageUpdated = computed(() => {
    return isPublishDatetimeUpdated.value || isDatetimeUpdated.value
  })

  // T A B L E S
  function tableById(tableId: PerformanceTableId): PerformanceTableRead | undefined {
    return _.find(updatedPerformance.value!.tables, { id: tableId })
  }
  function getTableUpdateStatus(table: PerformanceTableRead): TableStatus {
    if (!selectedPerformance.value) return TABLE_UPDATE_STATUS.AVAILABLE

    const originalTable = performanceReservationStore.tableById(table.id)
    if (!originalTable) return TABLE_UPDATE_STATUS.NEW
    return !_.isEqual(originalTable, table)
      ? TABLE_UPDATE_STATUS.UPDATED
      : TABLE_UPDATE_STATUS.AVAILABLE
  }
  const tablesWithUpdates = computed<PerformanceTableWithReservations[]>(() => {
    if (!updatedPerformance.value || !selectedPerformance.value) return []
    return updatedPerformance.value.tables.map(table => {
      const activeReservation = _.find(
        performanceReservationStore.activeReservations,
        reservation => _.includes(reservation.tables, table.id),
      )
      return {
        ...table,
        reservationStatus: activeReservation
          ? activeReservation.status
          : TABLE_UPDATE_STATUS.AVAILABLE,
        updateStatus: getTableUpdateStatus(table),
        activeReservation: activeReservation || null,
        inactiveReservations: [],
        hidden: tableIdsToBeDeleted.value.includes(table.id),
        allowToDelete: typeof table.id === 'string' || !activeReservation,
        hasUpdates:
          typeof table.id !== 'string' &&
          !_.isEqual(performanceReservationStore.tableById(table.id), tableById(table.id)),
      }
    })
  })
  function updateTable(
    tableUpdates: Pick<PerformanceTableRead, 'id'> & Partial<Omit<PerformanceTableRead, 'id'>>,
  ) {
    if (!updatedPerformance.value) return
    const table = tableById(tableUpdates.id)
    if (!table) return
    upsertById({ ...table, ..._.omit(tableUpdates, 'id') }, updatedPerformance.value.tables)
  }
  function resetTable(tableId: PerformanceTableId) {
    if (!updatedPerformance.value) return
    const originalTable = performanceReservationStore.tableById(tableId)
    if (!originalTable) return
    upsertById(originalTable, updatedPerformance.value.tables)
  }
  function createTable(): PerformanceTableRead | undefined {
    if (!updatedPerformance.value) return
    const settingStore = useSettingStore()
    const lastCreatedTable = _.last(
      updatedPerformance.value?.tables.filter(table => typeof table.id === 'string'),
    )
    return upsertById(
      {
        id: _.uniqueId('new_table_'),
        name: nextName(lastCreatedTable?.name || 'New Table 0'),
        type_id: settingStore.table_types[0]?.id || 0,
        performance: updatedPerformance.value.id,
        price_per_person: 20,
        available: true,
        available_guests: 1,
        held: false,
        minimum_guests: 1,
        maximum_guests: 1,
        shape: TableShape.SQUARE,
        x: 0,
        y: 0,
        w: 4,
        h: 4,
        rotation: 0,
        standing_room: false,
        ..._.omit(_.cloneDeep(lastCreatedTable), ['id', 'x', 'y', 'name']),
      },
      updatedPerformance.value.tables,
    )
  }
  function cloneTable(tableId: PerformanceTableId): PerformanceTableRead | undefined {
    if (!updatedPerformance.value) return
    const table = tableById(tableId)
    if (!table) return
    return upsertById(
      {
        id: _.uniqueId('new_table_'),
        name: nextName(table.name || 'New Table 0'),
        x: table.x + 1,
        y: table.y + 1,
        ..._.omit(_.cloneDeep(table), ['id', 'x', 'y', 'name']),
      },
      updatedPerformance.value.tables,
    )
  }
  function updateTablesByPrice(newPrice: number, oldPrice: number) {
    if (!updatedPerformance.value) return
    updatedPerformance.value.tables.forEach(table => {
      if (table.price_per_person === oldPrice.toFixed(2))
        table.price_per_person = newPrice.toFixed(2)
    })
  }
  const tableIdsToBeDeleted = ref<PerformanceTableId[]>([])
  function toggleTableIdToBeDeleted(tableId: PerformanceTableId) {
    const index = tableIdsToBeDeleted.value.findIndex(id => id === tableId)
    if (index !== -1) {
      tableIdsToBeDeleted.value.splice(index, 1)
    } else {
      tableIdsToBeDeleted.value.push(tableId)
    }
  }
  function removeTableFromPerformance(tableId: PerformanceTableId) {
    if (!updatedPerformance.value) return
    deleteById(tableId, updatedPerformance.value.tables)
  }
  function handleTableDeletion(tableId: PerformanceTableId) {
    if (typeof tableId === 'string') removeTableFromPerformance(tableId)
    else toggleTableIdToBeDeleted(tableId)
    // It is not removed from the performance until the performance is saved
    // This way we can still access the table info and make changes to it
  }
  const isPerformanceTablePageUpdated = computed(() => {
    return (
      tablesWithUpdates.value.some(table => table.updateStatus !== TABLE_UPDATE_STATUS.AVAILABLE) ||
      tableIdsToBeDeleted.value.length > 0
    )
  })

  // S E A T I N G   T I M E S
  const seatingTimeIdsToBeDeleted = ref<number[]>([])
  function handleSeatingTimeDeletion(seatingTimeId: PerformanceSeatingTimeId) {
    if (typeof seatingTimeId === 'string') removeSeatingTimeFromPerformance(seatingTimeId)
    else toggleSeatingTimeIdToBeDeleted(seatingTimeId)
    // It is not removed from the performance until the performance is saved
    // This way we can still access the seating time info and make changes to it
  }
  function removeSeatingTimeFromPerformance(seatingTimeId: PerformanceSeatingTimeId) {
    if (!updatedPerformance.value) return
    deleteById(seatingTimeId, updatedPerformance.value.seating_times)
  }
  function toggleSeatingTimeIdToBeDeleted(seatingTimeId: PerformanceSeatingTimeId) {
    const index = seatingTimeIdsToBeDeleted.value.findIndex(id => id === seatingTimeId)
    if (index !== -1) {
      seatingTimeIdsToBeDeleted.value.splice(index, 1)
    } else {
      seatingTimeIdsToBeDeleted.value.push(seatingTimeId)
    }
  }
  function createSeatingTime() {
    return upsertById(
      {
        id: _.uniqueId('new_seating_time_'),
        time: useSettingStore().makeDateTime({ time: '00:00' }),
        maximum_guests: 0,
      },
      updatedPerformance.value!.seating_times,
    )
  }
  function updateSeatingTime(seatingTime: PerformanceSeatingTimeRead) {
    upsertById(seatingTime, updatedPerformance.value!.seating_times)
  }
  function resetSeatingTimes() {
    updatedPerformance.value!.seating_times = _.cloneDeep(selectedPerformance.value!.seating_times)
    seatingTimeIdsToBeDeleted.value = []
  }
  const isSeatingTimePageUpdated = computed(() => {
    if (!updatedPerformance.value || !selectedPerformance.value) return false
    return (
      !_.isEqual(
        updatedPerformance.value!.seating_times,
        selectedPerformance.value!.seating_times,
      ) || seatingTimeIdsToBeDeleted.value.length > 0
    )
  })

  // A D D - O N S
  function addOnById(addOnId: PerformanceAddOnId): PerformanceAddOnRead | undefined {
    return _.find(updatedPerformance.value!.add_ons, { id: addOnId })
  }
  function updateAddOn(
    addOnUpdates: Pick<PerformanceAddOnRead, 'id'> & Partial<Omit<PerformanceAddOnRead, 'id'>>,
  ) {
    if (!updatedPerformance.value) return
    const addOn = addOnById(addOnUpdates.id)
    if (!addOn) return
    upsertById(
      { ...addOn, ..._.omit(addOnUpdates, ['id', 'status']) },
      updatedPerformance.value.add_ons,
    )
  }
  function resetAllAddOns() {
    updatedPerformance.value!.add_ons = _.cloneDeep(selectedPerformance.value!.add_ons)
  }
  function addAddOn(addOn: AddOnRead) {
    if (!updatedPerformance.value) return
    upsertById({ ...addOn, is_active: true }, updatedPerformance.value.add_ons)
  }
  function removeAddOnFromPerformance(addOnId: PerformanceAddOnId) {
    if (!updatedPerformance.value) return
    deleteById(addOnId, updatedPerformance.value.add_ons)
  }
  const isAddOnsPageUpdated = computed<boolean>(() => {
    if (!updatedPerformance.value || !selectedPerformance.value) return false
    return !_.isEqual(updatedPerformance.value!.add_ons, selectedPerformance.value!.add_ons)
  })

  const hasPerformanceUpdates = computed<boolean>(() => {
    return (
      !_.isEqual(selectedPerformance.value, updatedPerformance.value) ||
      isPerformanceTablePageUpdated.value ||
      isSeatingTimePageUpdated.value
    )
  })

  const discardUpdates = () => {
    updatedPerformance.value = _.cloneDeep(selectedPerformance.value)
    tableIdsToBeDeleted.value = []
  }
  const isSaving = ref(false)
  async function saveUpdates() {
    if (!updatedPerformance.value || !hasPerformanceUpdates.value) return
    try {
      isSaving.value = true
      const cleanPerformance = _.cloneDeep(updatedPerformance.value)
      cleanPerformance.tables.forEach(table => {
        if (typeof table.id === 'string') delete table.id
      })
      cleanPerformance.tables = cleanPerformance.tables.filter(
        table => !tableIdsToBeDeleted.value.includes(table.id),
      )
      cleanPerformance.seating_times.forEach(seatingTime => {
        if (typeof seatingTime.id === 'string') delete seatingTime.id
      })
      cleanPerformance.seating_times = cleanPerformance.seating_times.filter(
        seatingTime => !seatingTimeIdsToBeDeleted.value.includes(seatingTime.id),
      )
      const { data: performanceResponse } = await api.updatePerformanceDetailed(cleanPerformance)
      performanceReservationStore.setSelectedPerformance(performanceResponse)
      tableIdsToBeDeleted.value = []
      seatingTimeIdsToBeDeleted.value = []
    } finally {
      isSaving.value = false
    }
  }

  return {
    updatedPerformance,
    isDatetimeUpdated,
    isPublishDatetimeUpdated,
    isSchedulePageUpdated,
    hasPerformanceUpdates,
    discardUpdates,
    saveUpdates,
    isSaving,
    tablesWithUpdates,
    getTableUpdateStatus,
    resetTable,
    updateTable,
    createTable,
    cloneTable,
    updateTablesByPrice,
    isPerformanceTablePageUpdated,
    handleTableDeletion,
    tableIdsToBeDeleted,
    seatingTimeIdsToBeDeleted,
    handleSeatingTimeDeletion,
    updateSeatingTime,
    resetSeatingTimes,
    createSeatingTime,
    isSeatingTimePageUpdated,
    updateAddOn,
    isAddOnsPageUpdated,
    resetAllAddOns,
    addAddOn,
    removeAddOnFromPerformance,
  }
})
